// How a bill's revenue share came to be the number it is — and what to draw
// when it has no number at all.
//
// A share is RESOLVED rather than stamped: an override if the clause or the
// deal has one, else what an issued invoice froze, else the governing
// agreement's term in force on the period — and null when none of the three
// resolves. Several surfaces render that same fact (the month close, the
// invoice sheet, the regions report's totals), so the vocabulary lives here
// instead of a re-invention per table.
//
// Two rules these components exist to keep:
//
//   * AN UNRESOLVED SPLIT IS NEVER A ZERO. `applied_split_pct` and
//     `estimated_our_share_minor` are null together on a bill nobody can price,
//     and rendering that as `0%` / `$0.00` would put a plausible-looking figure
//     with no basis on screen — the exact failure 0013 exists to kill. So it
//     reads as a warning, in the warn tone this app already uses for a state
//     somebody has to go and fix, and never as an amount.
//   * THE ORDINARY CASE STAYS QUIET. `agreement` is what almost every unbilled
//     row is; it renders as the bare percentage it always did. `override` and
//     `frozen` are the two that change how a reader should treat the number —
//     one was typed by a person, the other is what an invoice already said — so
//     those, and only those, earn a mark beside it.
//
// The `—` treatment the actuals use was deliberately NOT reused for the
// unresolved case. "Nobody has recorded this yet" is an ordinary state of an
// ordinary row; "this bill cannot be priced" is a data gap that silently drops
// the row out of every our-share total on the screen, and the two must not look
// the same.

import type { ReactNode } from "react";
import styles from "./SplitSource.module.css";
import type { SplitSource } from "@crm/shared";
import { Badge } from "../design/Badge.js";
import { formatCount, formatPercent } from "../format/money.js";
import { splitSourceLabel, splitSourceReason } from "../format/labels.js";

/** The three sources that actually produced a percentage. */
export type ResolvedSplitSource = Exclude<SplitSource, "unresolved">;

/**
 * Rendered where our share — or the percentage under it — would be, on a bill
 * whose split does not resolve.
 *
 * A word rather than a dash, and a warn-toned one: the row is a worklist entry.
 * The reason rides in the `title` because the cell has room for neither, and
 * `splitSourceReason` is the same sentence the editor's disabled field carries.
 */
export function UnresolvedSplit(): ReactNode {
  return (
    <span className={styles.unresolved} title={splitSourceReason("unresolved")}>
      {splitSourceLabel("unresolved")}
    </span>
  );
}

/**
 * The split beside our share: the percentage, plus the provenance when the
 * provenance is worth knowing.
 *
 * `agreement` renders exactly as this annotation always did — a muted
 * percentage — because a mark on the ordinary case is a mark that stops meaning
 * anything. The other two carry a tag, so "this was corrected by hand" and
 * "this is what the invoice said" are legible at a glance rather than only in a
 * tooltip.
 */
export function SplitMark({
  source,
  pct,
}: {
  source: ResolvedSplitSource;
  pct: number;
}): ReactNode {
  return (
    <span className={styles.mark} title={splitSourceReason(source)}>
      <span className={styles.pct}>{formatPercent(pct)}</span>
      {source !== "agreement" && (
        <Badge shape="tag" tone={source === "override" ? "brand" : "slate"}>
          {splitSourceLabel(source).toLowerCase()}
        </Badge>
      )}
    </span>
  );
}

/**
 * Rendered where an amount would be, on a bill whose figure is not known yet
 * (migration 0017).
 *
 * A THIRD treatment, deliberately distinct from both of the two that already
 * exist on this screen, because it is a third fact:
 *
 *   `—`            an actual nobody has recorded. Ordinary, expected, no action.
 *   `Unresolved`   a split nothing can derive. A data gap, warn-toned.
 *   `Pending`      an amount somebody owes us. A live piece of work, and the
 *                  only one of the three with a person's name attached to it.
 *
 * Drawn as an empty slot rather than a word or a dash: a dashed outline reads as
 * a field waiting to be filled, which is exactly what it is, and it cannot be
 * misread as a figure the way a `0.00` or even a `—` in a money column can.
 * The `title` carries whoever it is waiting on when the row names one, so the
 * answer to "why is this blank" is one hover away rather than one screen away.
 */
export function PendingAmount({ waitingOn }: { waitingOn?: string | null }): ReactNode {
  const reason =
    waitingOn == null
      ? "No amount yet. This bill is certain; its figure is not, so it is left out of every total on this screen rather than counted as zero."
      : `No amount yet — waiting on ${waitingOn}. It is left out of every total on this screen rather than counted as zero.`;
  return (
    <span className={styles.pending} title={reason}>
      <span className={styles.srOnly}>Amount pending</span>
      <span aria-hidden="true">&nbsp;</span>
    </span>
  );
}

/**
 * How many bills a total left out because nobody has priced them yet — the
 * pending-amount twin of `UnresolvedCount`, and separate from it for the reason
 * `totals.ts` keeps the two counts apart: an unresolved split needs an agreement
 * term, a pending amount needs somebody to send a number, and one pile of "N
 * missing" would hide which.
 */
export function PendingCount({ count, of }: { count: number; of: string }): ReactNode {
  if (count <= 0) return null;
  return (
    <span
      className={styles.count}
      title={`${formatCount(count)} of the ${of} here have no amount yet, so they are in neither figure. Filter the billing schedule to pending amounts to see what each one is waiting on.`}
    >
      {`${formatCount(count)} pending`}
    </span>
  );
}

/**
 * How many bills a total quietly left out — the `unresolved_item_count` every
 * our-share figure in the reporting contract now carries beside it.
 *
 * Nothing at all when the count is zero, which is the common case and means
 * "this figure covers every item in the bucket". Anything else and the sum is a
 * total of SOME of the bills, which is not a thing to leave to a reader to
 * discover by reconciling it against the gross.
 */
export function UnresolvedCount({ count, of }: { count: number; of: string }): ReactNode {
  if (count <= 0) return null;
  return (
    <span
      className={styles.count}
      title={`${formatCount(count)} of the ${of} here have no resolvable split, so they are counted in the gross figures and left out of every our-share figure. Open the billing schedule filtered to unresolved splits to work through them.`}
    >
      {`${formatCount(count)} unpriced`}
    </span>
  );
}
